/* Reference shared primitives (ported from vault-app Common.jsx) → TSX, wired to our VaultCtx. */
import { useEffect, useState, type CSSProperties, type MouseEvent as ReactMouseEvent, type ReactNode } from "react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { EASE, SPRING_PRESS, cardItem } from "@/shared/motion/transitions";
import { useApp } from "./ctx";
import { ART as ART_ } from "./refData";
import Ic from "./Ic";
import { useAuth } from "@/features/auth/context/AuthContext";
import { starModel, type VaultModel } from "@/features/vault/vaultApi";

const ART = ART_ as unknown as Record<string, (m?: unknown) => string>;

// ── Real-model → reference-card display mappers (used when a VaultModel is passed) ──
const FMT_COLOR: Record<string, string> = {
  glb: "#33608F", gltf: "#33608F", "3dm": "#7A5C2E",
  stl: "#5B5F66", obj: "#5B5F66", fbx: "#5B5F66", ply: "#5B5F66",
  dwg: "#5B5F66", stp: "#5B5F66", step: "#5B5F66",
  png: "#2E7D4F", jpg: "#2E7D4F", jpeg: "#2E7D4F", webp: "#2E7D4F",
  mp4: "#8F4E8B", webm: "#8F4E8B", gif: "#8F4E8B",
};
const icoColor = (fmt = "") => FMT_COLOR[fmt.toLowerCase()] ?? "#7A5C2E";

function fmtSize(bytes?: number): string {
  if (!bytes) return "";
  const u = ["B", "KB", "MB", "GB"];
  let v = bytes, i = 0;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${u[i]}`;
}

/** Poster-less models fall back to a procedural thumb; pick an art that fits the piece. */
function artFor(m: VaultModel): string {
  const t = `${m.category ?? ""} ${m.name} ${m.tags.join(" ")}`.toLowerCase();
  if (/neck|pendant|chain/.test(t)) return "necklace";
  if (/ear|hoop|stud/.test(t)) return "earring";
  if (/brace|bangle|cuff|tennis/.test(t)) return "bracelet";
  if (/emerald/.test(t)) return "emerald";
  if (/stone|gem|diamond|solitaire|loose/.test(t)) return "diaSol";
  return "ringGold";
}

function metaFor(m: VaultModel): string {
  const size = fmtSize(m.glbSize || m.size);
  const d = m.updatedAt ? new Date(m.updatedAt).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "";
  return [size, d].filter(Boolean).join(" · ") || (m.status !== "ready" ? m.status : "");
}

export { default as SvgDefs } from "./SvgDefs";

export interface FileData {
  art: string; bg: string; pill: string; ico: string; ic: string;
  name: string; meta?: ReactNode; rs?: string; rsTxt?: string;
}

/** Jewellery thumbnail (bg gradient + shadow + procedural art). */
export function Thumb({ art, bg = "bgWarm", style, className = "thumb", children }: {
  art: string; bg?: string; style?: CSSProperties; className?: string; children?: ReactNode;
}) {
  const inner = `<rect width="200" height="140" fill="url(#${bg})"/><ellipse cx="100" cy="124" rx="46" ry="7" fill="#000" opacity=".10"/>${ART[art] ? ART[art]() : ""}`;
  return (
    <div className={className} style={style}>
      <svg className="art" viewBox="0 0 200 140" preserveAspectRatio="xMidYMid slice" dangerouslySetInnerHTML={{ __html: inner }} />
      {children}
    </div>
  );
}

/**
 * File card with select / share / render hover actions.
 *
 * Two modes, same DOM/classes:
 *  - demo mode: pass `f` (the reference FileData shape) — unchanged 1:1.
 *  - real mode: pass a `model` (VaultModel) — thumb uses model.posterUrl (falling
 *    back to the procedural Thumb), name/format drive the pill + mini-ico, clicking
 *    opens the real preview modal (setModalData("preview", model) + open("preview")),
 *    and a Star hover action toggles starModel. `onChanged` lets a page refetch
 *    (e.g. the Starred list drops an unstarred card).
 * `onOpen` overrides the default preview.
 */
export function FileCard({ f, model, badge, onOpen, onChanged, index }: {
  f?: FileData; model?: VaultModel; badge?: ReactNode; onOpen?: () => void; onChanged?: () => void;
  /** Position in a stagger grid — drives the entrance delay. Omit for standalone cards. */
  index?: number;
}) {
  const { open, go, selected, toggleSel, setModalData } = useApp();
  const { user } = useAuth();
  const reduce = useReducedMotion();
  // starredBy holds EVERY member who starred this shared-drive model; a star is
  // per-user, so mine = the array containing my id (not just non-empty).
  const [starred, setStarred] = useState(!!user && !!model?.starredBy?.includes(user.id));

  const selKey = model ? model._id : f!.name;
  const sel = selected.includes(selKey);

  const pill = model ? (model.format || "").toUpperCase() : f!.pill;
  const ic = model ? (model.format || "").toUpperCase().slice(0, 4) : f!.ic;
  const ico = model ? icoColor(model.format) : f!.ico;
  const name = model ? model.name : f!.name;
  const meta = model ? metaFor(model) : f!.meta;

  const openIt = () => {
    if (onOpen) return onOpen();
    if (model) setModalData("preview", model);
    open("preview");
  };
  const share = (e: ReactMouseEvent) => { e.stopPropagation(); if (model) setModalData("share", model); open("share"); };
  const more = (e: ReactMouseEvent) => { e.stopPropagation(); if (model) setModalData("ctx", model); open("ctx"); };
  const star = (e: ReactMouseEvent) => {
    e.stopPropagation();
    if (!model) return;
    const next = !starred;
    setStarred(next);
    starModel(model._id).then((r) => setStarred(r.starred)).catch(() => setStarred(!next)).finally(() => onChanged?.());
  };

  const overlay = (
    <>
      <span className="type-pill">{pill}</span>
      <button className="pick-chk" title="Select" onClick={(e) => { e.stopPropagation(); toggleSel(selKey); }}>✓</button>
      <div className="hover-acts">
        {model && (
          <button title={starred ? "Unstar" : "Star"} style={starred ? { color: "var(--accent-strong)" } : undefined} onClick={star}><Ic n="star" size={13} /></button>
        )}
        <button title="Share" onClick={share}><Ic n="link" size={13} /></button>
        <button title="Render settings" onClick={(e) => { e.stopPropagation(); go("render"); }}><Ic n="sliders" size={13} /></button>
        <button title="More" onClick={more}><Ic n="dots" size={13} /></button>
      </div>
    </>
  );

  return (
    <motion.div
      className={"file-card" + (sel ? " sel" : "")}
      onClick={openIt}
      variants={reduce ? undefined : cardItem}
      custom={index}
      whileHover={reduce ? undefined : { y: -3 }}
      transition={SPRING_PRESS}
    >
      {badge}
      {model && model.posterUrl ? (
        <div className="thumb">
          <img src={model.posterUrl} alt={name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          {overlay}
        </div>
      ) : (
        <Thumb art={model ? artFor(model) : f!.art} bg={model ? "bgWarm" : f!.bg}>{overlay}</Thumb>
      )}
      <div className="file-info">
        <span className="mini-ico" style={{ background: ico }}>{ic}</span>
        <div>
          <div className="fi-name">{name}</div>
          <div className="fi-meta">
            {meta}{!model && f!.rs ? <> · <span className={"badge " + f!.rs} style={{ fontSize: 9 }}>{f!.rsTxt}</span></> : null}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

/** Modal chrome. Renders only when modals[id] is true. */
export function Modal({ id, maxWidth = 520, title, head, bodyStyle, children, plain }: {
  id: string; maxWidth?: number; title?: ReactNode; head?: ReactNode;
  bodyStyle?: CSSProperties; children?: ReactNode; plain?: boolean;
}) {
  const { modals, close } = useApp();
  const reduce = useReducedMotion();
  return (
    <AnimatePresence>
      {modals[id] && (
        <motion.div
          className="overlay open"
          onClick={(e) => { if (e.target === e.currentTarget) close(id); }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18, ease: EASE }}
        >
          <motion.div
            className="modal"
            // animation:"none" disables the CSS `pop` keyframe so framer owns the entrance.
            style={{ maxWidth, animation: "none" }}
            initial={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.96, y: 8 }}
            animate={reduce ? { opacity: 1 } : { opacity: 1, scale: 1, y: 0 }}
            exit={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.24, ease: EASE }}
          >
            {!plain && (
              <div className="m-head">
                <h2>{title}</h2>
                {head}
                <button className="m-close" onClick={() => close(id)}>✕</button>
              </div>
            )}
            <div className="m-body" style={bodyStyle}>{children}</div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/** Smooth toggle. */
export function Switch({ on = true, onChange }: { on?: boolean; onChange?: (v: boolean) => void }) {
  const [v, setV] = useState(on);
  // Follow the controlling prop so an optimistic parent that reverts on error
  // (e.g. Share's allow-download / make-public toggles) shows the true state.
  useEffect(() => { setV(on); }, [on]);
  return (
    <label className="switch">
      <input type="checkbox" checked={v} onChange={(e) => { setV(e.target.checked); onChange?.(e.target.checked); }} />
      <span className="tr"></span>
    </label>
  );
}

/** Pill option group (single or multi). */
export function Pills({ options, value, onChange, multi }: {
  options: string[]; value?: string | string[]; onChange?: (v: string | string[]) => void; multi?: boolean;
}) {
  const [local, setLocal] = useState<string | string[]>(value ?? (multi ? options.slice(0, 1) : options[0]));
  const cur = value !== undefined && onChange ? value : local;
  const set = (onChange ?? setLocal) as (v: string | string[]) => void;
  const isOn = (o: string) => (multi ? (cur as string[]).includes(o) : cur === o);
  const click = (o: string) => (multi ? set(isOn(o) ? (cur as string[]).filter((x) => x !== o) : [...(cur as string[]), o]) : set(o));
  return (
    <div className="pill-opts">
      {options.map((o) => (
        <button key={o} className={isOn(o) ? "on" : ""} onClick={() => click(o)}>{o}</button>
      ))}
    </div>
  );
}

/** Copy button with flash. Pass `value` to copy it to the clipboard on click. */
export function CopyBtn({ className = "btn gold", label = "Copy link", value }: { className?: string; label?: string; value?: string }) {
  const [ok, setOk] = useState(false);
  const click = () => {
    if (value) void navigator.clipboard?.writeText(value);
    setOk(true);
    setTimeout(() => setOk(false), 1400);
  };
  return (
    <button className={className + (ok ? " copy-flash" : "")} onClick={click}>
      {ok ? "✓ Copied" : label}
    </button>
  );
}

export function Kv({ k, v }: { k: ReactNode; v: ReactNode }) {
  return <div className="kv"><b>{k}</b><span>{v}</span></div>;
}

export function ToggleRow({ title, sub, right, on }: { title: ReactNode; sub?: ReactNode; right?: ReactNode; on?: boolean }) {
  return (
    <div className="toggle-row">
      <div className="t-txt"><b>{title}</b><span>{sub}</span></div>
      {right ?? <Switch on={on} />}
    </div>
  );
}
